<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
  assistantId: {
    type: [Number, String],
    default: null,
  },
});
const store = useStore();
const { t } = useI18n();

const useDefaultData = ref(true);
const viewMode = ref('grid');
const selectedCountry = ref('');
const selectedLanguage = ref('');
const isLoading = ref(false);
const isSaving = ref(false);

// Use product from props to sync use_default_data
const productData = computed(() => props.product || {});

// Modal state
const showModal = ref(false);
const editingItem = ref(null);
const formData = ref({
  folder_name: '',
  description: '',
  image_url: '',
  video_url: '',
  country: '',
  language: '',
});

const countryOptions = computed(() => [
  {
    value: '',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.ALL'),
  },
  {
    value: 'MY',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.MY'),
  },
  {
    value: 'ID',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.ID'),
  },
  {
    value: 'SG',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.SG'),
  },
  {
    value: 'TH',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.COUNTRY.TH'),
  },
]);

const languageOptions = computed(() => [
  {
    value: '',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.ALL'),
  },
  {
    value: 'en',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.EN'),
  },
  {
    value: 'zh',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.ZH'),
  },
  {
    value: 'ms',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.MS'),
  },
  {
    value: 'th',
    label: t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FILTERS.LANGUAGE.TH'),
  },
]);

// Get testimonials from store
const storeTestimonials = computed(
  () => store.getters['aiCloser/getTestimonials']
);

// Local testimonials
const testimonials = ref([]);

// Fetch testimonials on mount
onMounted(async () => {
  if (!props.assistantId) return;
  // Use product from props
  if (props.product?.use_default_data !== undefined) {
    useDefaultData.value = props.product.use_default_data;
  }
  await handleRefresh();
});

// Sync use_default_data from product props
watch(
  () => props.product?.use_default_data,
  newVal => {
    if (newVal !== undefined) {
      useDefaultData.value = newVal;
    }
  },
  { immediate: true }
);

// Sync store to local
watch(
  () => storeTestimonials.value,
  newVal => {
    testimonials.value = (newVal || []).map(t => ({
      id: t.id,
      folder: t.folder_name || '',
      description: t.description || '',
      imageUrl: t.image_url || '',
      videoUrl: t.video_url || '',
      type: t.video_url ? 'video' : 'image',
      url: t.image_url || t.video_url || '',
      country: t.country || '',
      language: t.language || '',
      createdAt: t.created_at ? new Date(t.created_at).toLocaleString() : '',
      updatedAt: t.updated_at ? new Date(t.updated_at).toLocaleString() : '',
    }));
  },
  { immediate: true, deep: true }
);

const filteredTestimonials = computed(() => {
  let items = testimonials.value;
  if (selectedCountry.value) {
    items = items.filter(t => t.country === selectedCountry.value);
  }
  if (selectedLanguage.value) {
    items = items.filter(t => t.language === selectedLanguage.value);
  }
  return items;
});

const handleRefresh = async () => {
  if (!props.assistantId) return;
  isLoading.value = true;
  try {
    await store.dispatch('aiCloser/fetchTestimonials', {
      assistantId: props.assistantId,
    });
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ALERTS.LOAD_FAILED')
    );
  } finally {
    isLoading.value = false;
  }
};

// Modal handlers
const openAddModal = () => {
  editingItem.value = null;
  formData.value = {
    folder_name: '',
    description: '',
    image_url: '',
    video_url: '',
    country: '',
    language: '',
  };
  showModal.value = true;
};

const openEditModal = item => {
  editingItem.value = item;
  formData.value = {
    folder_name: item.folder,
    description: item.description,
    image_url: item.imageUrl,
    video_url: item.videoUrl,
    country: item.country,
    language: item.language,
  };
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  editingItem.value = null;
};

const handleSave = async () => {
  if (!props.assistantId) return;
  if (!formData.value.description) {
    useAlert(
      t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ALERTS.DESCRIPTION_REQUIRED')
    );
    return;
  }
  if (!formData.value.image_url && !formData.value.video_url) {
    useAlert(
      t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ALERTS.MEDIA_URL_REQUIRED')
    );
    return;
  }

  isSaving.value = true;
  try {
    if (editingItem.value) {
      await store.dispatch('aiCloser/updateTestimonial', {
        assistantId: props.assistantId,
        testimonialId: editingItem.value.id,
        data: formData.value,
      });
      useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ALERTS.UPDATED'));
    } else {
      await store.dispatch('aiCloser/createTestimonial', {
        assistantId: props.assistantId,
        data: formData.value,
      });
      useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ALERTS.ADDED'));
    }
    closeModal();
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ALERTS.SAVE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async item => {
  if (
    !confirm(
      t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ALERTS.DELETE_CONFIRM')
    )
  )
    return;

  try {
    await store.dispatch('aiCloser/deleteTestimonial', {
      assistantId: props.assistantId,
      testimonialId: item.id,
    });
    useAlert(t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ALERTS.DELETED'));
  } catch (error) {
    useAlert(
      error?.message ||
        t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ALERTS.DELETE_FAILED')
    );
  }
};

const isFormValid = computed(() => {
  return (
    formData.value.description &&
    (formData.value.image_url || formData.value.video_url)
  );
});

const previewUrl = computed(() => {
  return formData.value.image_url || formData.value.video_url;
});

const previewType = computed(() => {
  return formData.value.video_url && !formData.value.image_url
    ? 'video'
    : 'image';
});
</script>

<template>
  <div class="testimonial-tab">
    <!-- Toggle Switch -->
    <div class="toggle-row">
      <button
        class="crt-switch"
        :class="{ 'is-on': useDefaultData }"
        type="button"
        @click="useDefaultData = !useDefaultData"
      >
        <span />
      </button>
      <span class="toggle-label">{{
        t('AILAB.ASSISTANT_V2.PRODUCT_INFO.TOGGLE.LABEL_DEFAULT')
      }}</span>
    </div>

    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <select v-model="selectedCountry" class="filter-select">
          <option
            v-for="opt in countryOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <select v-model="selectedLanguage" class="filter-select">
          <option
            v-for="opt in languageOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <button
          class="refresh-btn"
          :disabled="isLoading"
          @click="handleRefresh"
        >
          <span
            class="i-lucide-refresh-cw size-4"
            :class="[isLoading && 'animate-spin']"
          />
        </button>
      </div>
      <div class="toolbar-right">
        <button
          class="add-btn"
          :disabled="useDefaultData"
          @click="openAddModal"
        >
          <span class="i-lucide-plus size-4" />
          <span>{{
            t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ACTIONS.ADD')
          }}</span>
        </button>
        <div class="view-toggle">
          <button
            class="view-btn"
            :class="[viewMode === 'list' && 'active']"
            @click="viewMode = 'list'"
          >
            <span class="i-lucide-list size-4" />
          </button>
          <button
            class="view-btn"
            :class="[viewMode === 'grid' && 'active']"
            @click="viewMode = 'grid'"
          >
            <span class="i-lucide-layout-grid size-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Content Container -->
    <div class="content-container" :class="{ 'is-default': useDefaultData }">
      <!-- Default Watermark -->
      <div v-if="useDefaultData" class="default-watermark">
        {{ t('AILAB.ASSISTANT_V2.PRODUCT_INFO.DEFAULT_WATERMARK') }}
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="empty-state">
        <span class="i-lucide-loader-2 size-10 animate-spin text-orange-400" />
        <p class="empty-text">
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.STATE.LOADING') }}
        </p>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredTestimonials.length === 0" class="empty-state">
        <div class="empty-icon">
          <span class="i-lucide-message-square-quote size-8" />
        </div>
        <h4 class="empty-title">
          {{ t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.STATE.EMPTY_TITLE') }}
        </h4>
        <p class="empty-text">
          {{
            t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.STATE.EMPTY_SUBTITLE')
          }}
        </p>
        <button
          v-if="!useDefaultData"
          class="add-first-btn"
          @click="openAddModal"
        >
          <span class="i-lucide-plus size-4" />
          <span>{{
            t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.ACTIONS.ADD_FIRST')
          }}</span>
        </button>
      </div>

      <!-- Grid View -->
      <div v-else-if="viewMode === 'grid'" class="testimonial-grid">
        <div
          v-for="item in filteredTestimonials"
          :key="item.id"
          class="testimonial-card"
        >
          <div class="card-header">
            <div class="folder-info">
              <span class="i-lucide-folder-open size-4" />
              <span class="folder-name">{{
                item.folder ||
                t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FOLDER.UNCATEGORIZED')
              }}</span>
            </div>
            <div v-if="!useDefaultData" class="card-actions">
              <button class="action-btn edit" @click="openEditModal(item)">
                <span class="i-lucide-pencil size-3.5" />
              </button>
              <button class="action-btn delete" @click="handleDelete(item)">
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
          <div class="card-content">
            <div class="media-preview">
              <img
                v-if="item.type === 'image'"
                :src="item.url"
                :alt="item.description"
                class="preview-image"
              />
              <template v-else>
                <video :src="item.url" class="preview-video" />
                <div class="play-btn">
                  <span class="i-lucide-play size-5" />
                </div>
              </template>
            </div>
            <div class="testimonial-info">
              <p class="testimonial-desc">{{ item.description }}</p>
              <div class="testimonial-dates">
                <small>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.META.CREATED', {
                    date: item.createdAt,
                  })
                }}</small>
                <small>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.META.UPDATED', {
                    date: item.updatedAt,
                  })
                }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- List View -->
      <div v-else class="testimonial-list">
        <div
          v-for="item in filteredTestimonials"
          :key="item.id"
          class="testimonial-row"
        >
          <div class="media-thumb">
            <img
              v-if="item.type === 'image'"
              :src="item.url"
              :alt="item.description"
            />
            <template v-else>
              <video :src="item.url" />
              <span class="i-lucide-play size-4 play-icon" />
            </template>
          </div>
          <div class="testimonial-details">
            <div class="folder-info">
              <span class="i-lucide-folder-open size-3.5" />
              <span>{{
                item.folder ||
                t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FOLDER.UNCATEGORIZED')
              }}</span>
            </div>
            <p class="testimonial-desc">{{ item.description }}</p>
            <small class="testimonial-date">{{
              t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.META.UPDATED', {
                date: item.updatedAt,
              })
            }}</small>
          </div>
          <div v-if="!useDefaultData" class="row-actions">
            <button class="action-btn edit" @click="openEditModal(item)">
              <span class="i-lucide-pencil size-4" />
            </button>
            <button class="action-btn delete" @click="handleDelete(item)">
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content">
          <div class="modal-header">
            <h3>
              {{
                editingItem
                  ? t(
                      'AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.MODAL.EDIT_TITLE'
                    )
                  : t(
                      'AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.MODAL.ADD_TITLE'
                    )
              }}
            </h3>
            <button class="close-btn" @click="closeModal">
              <span class="i-lucide-x size-5" />
            </button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label
                >{{
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.FORM.DESCRIPTION.LABEL'
                  )
                }}
                <span class="required">*</span></label
              >
              <textarea
                v-model="formData.description"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.FORM.DESCRIPTION.PLACEHOLDER'
                  )
                "
                class="form-input form-textarea"
                rows="4"
              />
            </div>
            <div class="form-group">
              <label>{{
                t(
                  'AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.FORM.IMAGE_URL.LABEL'
                )
              }}</label>
              <input
                v-model="formData.image_url"
                type="url"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.FORM.IMAGE_URL.PLACEHOLDER'
                  )
                "
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>{{
                t(
                  'AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.FORM.VIDEO_URL.LABEL'
                )
              }}</label>
              <input
                v-model="formData.video_url"
                type="url"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.FORM.VIDEO_URL.PLACEHOLDER'
                  )
                "
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label>{{
                t('AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.FORM.FOLDER.LABEL')
              }}</label>
              <input
                v-model="formData.folder_name"
                type="text"
                :placeholder="
                  t(
                    'AILAB.ASSISTANT_V2.PRODUCT_TESTIMONIAL.FORM.FOLDER.PLACEHOLDER'
                  )
                "
                class="form-input"
              />
            </div>
            <div class="form-row">
              <div class="form-group">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.COUNTRY.LABEL')
                }}</label>
                <select v-model="formData.country" class="form-input">
                  <option
                    v-for="opt in countryOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label>{{
                  t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.LANGUAGE.LABEL')
                }}</label>
                <select v-model="formData.language" class="form-input">
                  <option
                    v-for="opt in languageOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
            </div>
            <!-- Preview -->
            <div v-if="previewUrl" class="preview-section">
              <label>{{
                t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.PREVIEW.LABEL')
              }}</label>
              <div class="preview-box">
                <img
                  v-if="previewType === 'image'"
                  :src="previewUrl"
                  :alt="
                    t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.FORM.PREVIEW.ALT')
                  "
                />
                <video v-else :src="previewUrl" controls />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="closeModal">
              {{ t('AILAB.FORM.CANCEL') }}
            </button>
            <button
              class="btn-save"
              :disabled="!isFormValid || isSaving"
              @click="handleSave"
            >
              <span
                v-if="isSaving"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span>
                {{
                  editingItem
                    ? t('AILAB.FORM.UPDATE')
                    : t('AILAB.ASSISTANT_V2.PRODUCT_MEDIA.MODAL.ACTIONS.ADD')
                }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.testimonial-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 80px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  inset: 0;
  background: var(--wlab-text-muted);
  border-radius: 24px;
  transition: 0.3s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: 0.3s;
}

.toggle-switch input:checked + .toggle-slider {
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

:global(body:not(.dark) .toggle-switch input:checked + .toggle-slider) {
  background: rgba(0, 102, 255, 0.25);
  border-color: rgba(0, 102, 255, 0.6);
}

:global(body.dark .toggle-switch input:checked + .toggle-slider) {
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
  background: #ffffff;
}

:global(body:not(.dark) .toggle-switch input:checked + .toggle-slider::before) {
  background: #0066ff;
}

:global(body.dark .toggle-switch input:checked + .toggle-slider::before) {
  background: #ffffff;
}

.toggle-label {
  font-size: 14px;
  color: var(--wlab-text-secondary);
}

/* Toolbar */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-select {
  padding: 8px 32px 8px 12px;
  font-size: 13px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  cursor: pointer;
}

.filter-select option {
  background: var(--wlab-bg-primary);
  color: var(--wlab-text-primary);
}

.refresh-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--wlab-bg-input);
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  color: var(--wlab-text-primary);
  cursor: pointer;
}

.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #ffffff;
  border: none;
  border-radius: 6px;
  color: #000000;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.view-toggle {
  display: flex;
  gap: 2px;
  background: var(--wlab-bg-tertiary);
  border-radius: 6px;
  padding: 2px;
}

.view-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--wlab-text-muted);
  cursor: pointer;
}

.view-btn.active {
  background: rgba(255, 255, 255, 0.2);
  color: #0066ff;
}

/* Content Container */
.content-container {
  position: relative;
  padding: 16px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  min-height: 200px;
}

.content-container.is-default {
  opacity: 0.8;
}

.default-watermark {
  position: absolute;
  right: 3%;
  bottom: 5%;
  font-size: 5rem;
  font-weight: 700;
  color: var(--wlab-border-light);
  pointer-events: none;
  z-index: 1;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  min-height: 200px;
}

.empty-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(0, 102, 255, 0.15) 0%,
    rgba(0, 102, 255, 0.05) 100%
  );
  border: 1px solid rgba(0, 102, 255, 0.2);
  color: #0066ff;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--wlab-text-primary);
  margin: 0 0 8px 0;
}

.empty-text {
  font-size: 14px;
  color: var(--wlab-text-muted);
  margin: 0;
}

.add-first-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  margin-top: 16px;
  background: #ffffff;
  border: none;
  border-radius: 8px;
  color: #000000;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-first-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

/* Grid View */
.testimonial-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  position: relative;
  z-index: 2;
}

.testimonial-card {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s;
}

.testimonial-card:hover {
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.card-header {
  padding: 12px;
  border-bottom: 1px solid var(--wlab-border-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.folder-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--wlab-text-secondary);
}

.folder-name {
  font-size: 13px;
  font-weight: 600;
}

.card-actions,
.row-actions {
  display: flex;
  gap: 4px;
}

.action-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn.edit {
  color: var(--wlab-text-secondary);
}

.action-btn.edit:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.3);
  color: #3b82f6;
}

.action-btn.delete {
  color: var(--wlab-text-muted);
}

.action-btn.delete:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
}

.card-content {
  padding: 12px;
  display: flex;
  gap: 12px;
}

.media-preview {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
}

.preview-image,
.preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  color: white;
}

.testimonial-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}

.testimonial-desc {
  font-size: 13px;
  color: var(--wlab-text-primary);
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.testimonial-dates {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
}

.testimonial-dates small {
  font-size: 11px;
  color: var(--wlab-text-muted);
}

/* List View */
.testimonial-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  z-index: 2;
}

.testimonial-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 12px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 8px;
  transition: all 0.2s;
}

.testimonial-row:hover {
  background: var(--wlab-border-light);
}

.media-thumb {
  width: 80px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
}

.media-thumb img,
.media-thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  background: rgba(255, 255, 255, 0.8);
  padding: 4px;
  border-radius: 50%;
}

.testimonial-details {
  flex: 1;
  min-width: 0;
}

.testimonial-details .folder-info {
  font-size: 12px;
  margin-bottom: 6px;
}

.testimonial-details .testimonial-desc {
  font-size: 13px;
  margin: 0 0 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.testimonial-date {
  font-size: 11px;
  color: var(--wlab-text-muted);
}

.row-actions {
  flex-shrink: 0;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  background: var(--wlab-bg-primary, #1a1a1a);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--wlab-border-light);
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--wlab-text-primary);
}

.close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--wlab-text-muted);
  cursor: pointer;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--wlab-text-primary);
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 13px;
  font-weight: 500;
  color: var(--wlab-text-secondary);
}

.required {
  color: #ef4444;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-input {
  padding: 10px 12px;
  font-size: 14px;
  color: var(--wlab-text-primary);
  background: var(--wlab-bg-input, #2a2a2a);
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  outline: none;
  transition: border-color 0.2s;
}

.form-input:focus {
  border-color: #ffffff;
}

.form-input::placeholder {
  color: var(--wlab-text-muted);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-section label {
  font-size: 13px;
  font-weight: 500;
  color: var(--wlab-text-secondary);
}

.preview-box {
  width: 100%;
  max-height: 200px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
}

.preview-box img,
.preview-box video {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: contain;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid var(--wlab-border-light);
}

.btn-cancel {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: var(--wlab-text-secondary);
  background: transparent;
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.05);
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  background: #ffffff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
